// usePersistedViewMode — state hook for the 2D ⇄ 3D toggle that survives
// reload. The key is chosen per-surface (`fw.viewMode.canvas`,
// `fw.viewMode.atlas`) so each surface remembers its own preference.
//
// SSR-safe: falls back to the default when `window` is undefined and
// silently no-ops on storage errors (private mode, quota, etc.) so a
// broken localStorage never crashes the canvas.

import { useCallback, useState } from 'react';

export type ViewMode = '2d' | '3d';

function load(storageKey: string, fallback: ViewMode): ViewMode {
  if (typeof window === 'undefined') return fallback;
  try {
    const saved = window.localStorage.getItem(storageKey);
    return saved === '2d' || saved === '3d' ? saved : fallback;
  } catch {
    return fallback;
  }
}

export function usePersistedViewMode(
  storageKey: string,
  defaultMode: ViewMode = '2d',
): [ViewMode, (m: ViewMode) => void] {
  const [mode, setMode] = useState<ViewMode>(() => load(storageKey, defaultMode));
  const set = useCallback(
    (m: ViewMode) => {
      setMode(m);
      if (typeof window === 'undefined') return;
      try {
        window.localStorage.setItem(storageKey, m);
      } catch {
        /* private mode / quota — keep the in-memory state */
      }
    },
    [storageKey],
  );
  return [mode, set];
}
